import React, { useEffect, useState } from 'react';
import { FileText, SpinnerGap, Check, ArrowSquareOut, X, MagnifyingGlass } from '@phosphor-icons/react';
import * as api from '../api/client';

const DROPLOFT_SETUP_URL = 'https://stage-a.droploft.ai/settings';

interface DroploftPanelProps {
  open: boolean;
  onClose: () => void;
  deckId?: string;
  onImported?: (titles: string[]) => void;
}

export default function DroploftPanel({ open, onClose, deckId, onImported }: DroploftPanelProps) {
  const [connections, setConnections] = useState<api.DroploftConnection[]>([]);
  const [loadingConn, setLoadingConn] = useState(false);
  const [docs, setDocs] = useState<api.DroploftDocument[]>([]);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [connecting, setConnecting] = useState(false);

  const hasConnection = connections.length > 0;
  const connId = connections[0]?.id;

  const refreshConnections = async () => {
    setLoadingConn(true);
    try {
      const c = await api.listDroploftConnections();
      setConnections(c);
    } catch {
      setConnections([]);
    } finally {
      setLoadingConn(false);
    }
  };

  const runSearch = async () => {
    if (!deckId || !hasConnection) return;
    setSearching(true);
    setError(null);
    try {
      const result = await api.searchDroploftDocuments(deckId, query, connId, 50);
      setDocs(result);
    } catch (err: any) {
      setError(err?.message || 'Search failed');
      setDocs([]);
    } finally {
      setSearching(false);
    }
  };

  useEffect(() => {
    if (open) {
      refreshConnections();
      setSelectedIds(new Set());
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && hasConnection && deckId) runSearch();
  }, [open, hasConnection, deckId]);

  useEffect(() => {
    if (!open) return;
    const handleEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [open, onClose]);

  const handleConnect = async () => {
    const trimmed = apiKey.trim();
    if (trimmed.length < 8) {
      setError('Paste the full API key.');
      return;
    }
    setConnecting(true);
    setError(null);
    try {
      await api.addDroploftConnection({ apiKey: trimmed });
      setApiKey('');
      await refreshConnections();
    } catch (err: any) {
      setError(err?.message || 'Failed to add Droploft connection');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!connId) return;
    await api.deleteDroploftConnection(connId);
    setConnections([]);
    setDocs([]);
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!deckId || selectedIds.size === 0) return;
    setSubmitting(true);
    const titles: string[] = [];
    for (const id of selectedIds) {
      setImportingIds(prev => new Set(prev).add(id));
      try {
        const r = await api.importDroploftDocument(deckId, id, connId);
        if (r?.title) titles.push(r.title);
      } catch (err: any) {
        setError(err?.message || `Import failed for ${id}`);
      } finally {
        setImportingIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      }
    }
    setSubmitting(false);
    setSelectedIds(new Set());
    onClose();
    onImported?.(titles);
  };

  if (!open) return null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.32)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}
      />

      <div style={{
        position: 'relative', width: 560, maxWidth: '92vw',
        height: hasConnection ? 540 : 'auto', maxHeight: '78vh',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)', borderRadius: 12,
        boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 28px',
          borderBottom: hasConnection ? '1px solid var(--border)' : 'none',
          flexShrink: 0,
        }}>
          <span style={{
            fontSize: 'var(--fs-md)', fontWeight: 400,
            color: 'var(--text-primary)', letterSpacing: '0.01em',
          }}>Droploft</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {hasConnection && (
              <button
                onClick={handleDisconnect}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: 'none', background: 'transparent',
                  color: 'var(--text-muted)', cursor: 'pointer',
                  fontSize: 'var(--fs-xs)', fontWeight: 300,
                  fontFamily: 'inherit', letterSpacing: '0.02em',
                }}
              >Disconnect</button>
            )}
            <button
              onClick={onClose}
              style={{
                width: 24, height: 24, borderRadius: 6, border: 'none', background: 'transparent',
                color: 'var(--text-muted)', cursor: 'pointer', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}
              aria-label="Close"
            ><X size={14} weight="light" /></button>
          </div>
        </div>

        {/* Body */}
        {loadingConn ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
            <SpinnerGap size={20} weight="light" className="animate-spin" />
          </div>
        ) : !hasConnection ? (
          <div style={{
            padding: '24px 32px 32px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
          }}>
            <div style={{
              width: 48, height: 48, borderRadius: '50%',
              border: '1px solid var(--border-strong)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'var(--text-muted)', marginBottom: 20,
            }}><FileText size={20} weight="thin" /></div>
            <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 300, color: 'var(--text-primary)', marginBottom: 6 }}>
              Connect Droploft
            </div>
            <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 300, color: 'var(--text-muted)', maxWidth: 360, lineHeight: 1.6, marginBottom: 18 }}>
              Generate an API key in Droploft, then paste it below.
            </div>
            <a
              href={DROPLOFT_SETUP_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 6,
                background: 'transparent', border: '1px solid var(--border)',
                color: 'var(--text-primary)', textDecoration: 'none',
                fontSize: 'var(--fs-sm)', marginBottom: 14,
              }}
            >
              <ArrowSquareOut size={13} weight="light" />
              Open Droploft settings
            </a>
            <input
              type="password"
              value={apiKey}
              onChange={e => setApiKey(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleConnect(); }}
              placeholder="Paste API key"
              autoFocus
              style={{
                width: '100%', maxWidth: 360,
                padding: '9px 12px', fontSize: 'var(--fs-base)',
                border: '1px solid var(--border)', borderRadius: 6,
                background: 'var(--bg-primary)', color: 'var(--text-primary)',
                fontFamily: 'inherit', outline: 'none', marginBottom: 14,
              }}
            />
            <button
              onClick={handleConnect}
              disabled={connecting || apiKey.trim().length < 8}
              style={{
                padding: '10px 22px', borderRadius: 8,
                background: connecting || apiKey.trim().length < 8 ? 'var(--border)' : 'var(--brand)',
                color: '#fff', border: 'none',
                cursor: connecting || apiKey.trim().length < 8 ? 'default' : 'pointer',
                fontSize: 'var(--fs-sm)', fontWeight: 500,
                fontFamily: 'inherit', letterSpacing: '0.02em',
              }}
            >{connecting ? 'Verifying…' : 'Save'}</button>
            {error && (
              <div style={{ marginTop: 16, fontSize: 'var(--fs-xs)', color: '#e55' }}>{error}</div>
            )}
          </div>
        ) : (
          <>
            {/* Search bar */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <MagnifyingGlass size={14} weight="light" style={{ color: 'var(--text-muted)' }} />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
                placeholder="Search Droploft documents…"
                style={{
                  flex: 1, border: 'none', outline: 'none', background: 'transparent',
                  fontSize: 'var(--fs-base)', color: 'var(--text-primary)', fontFamily: 'inherit',
                }}
              />
              <button
                onClick={runSearch}
                disabled={searching}
                style={{
                  padding: '4px 10px', borderRadius: 6, border: '1px solid var(--border)',
                  background: 'transparent', color: 'var(--text-secondary)',
                  cursor: searching ? 'default' : 'pointer', fontSize: 'var(--fs-xs)',
                  fontFamily: 'inherit',
                }}
              >{searching ? 'Searching…' : 'Search'}</button>
            </div>

            {/* Doc list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {searching ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
                  <SpinnerGap size={16} weight="light" className="animate-spin" />
                </div>
              ) : docs.length === 0 ? (
                <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
                  {error || 'No documents found.'}
                </div>
              ) : docs.map(doc => {
                const id = doc.id;
                const isSelected = selectedIds.has(id);
                const isImporting = importingIds.has(id);
                return (
                  <div
                    key={id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 16px',
                      borderBottom: '1px solid var(--border)',
                      fontSize: 'var(--fs-base)', fontWeight: 300,
                      color: 'var(--text-secondary)',
                    }}
                  >
                    <span
                      onClick={() => toggleSelect(id)}
                      style={{
                        width: 14, height: 14, borderRadius: 3, flexShrink: 0,
                        border: `1px solid ${isSelected ? 'var(--brand)' : 'var(--border-strong)'}`,
                        background: isSelected ? 'var(--brand)' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer',
                      }}
                    >
                      {isSelected && <Check size={9} weight="bold" style={{ color: '#fff' }} />}
                    </span>
                    <FileText size={14} weight="light" style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={doc.title}>
                      {doc.title || doc.slug || id}
                    </span>
                    {isImporting ? (
                      <SpinnerGap size={11} weight="light" className="animate-spin" />
                    ) : doc.url ? (
                      <a
                        href={doc.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ color: 'var(--text-muted)', display: 'flex' }}
                      ><ArrowSquareOut size={12} weight="light" /></a>
                    ) : null}
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <div style={{
              padding: '12px 16px', borderTop: '1px solid var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexShrink: 0,
            }}>
              <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>
                {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select documents to import'}
              </span>
              <button
                onClick={handleConfirm}
                disabled={submitting || selectedIds.size === 0}
                style={{
                  padding: '6px 16px', borderRadius: 6, border: 'none',
                  background: selectedIds.size === 0 ? 'var(--border)' : 'var(--brand)',
                  color: '#fff', fontSize: 'var(--fs-sm)', fontWeight: 500,
                  cursor: submitting || selectedIds.size === 0 ? 'default' : 'pointer',
                  fontFamily: 'inherit',
                }}
              >{submitting ? 'Importing…' : 'Import'}</button>
            </div>
            {error && hasConnection && (
              <div style={{ padding: '8px 16px', fontSize: 'var(--fs-xs)', color: '#e55', borderTop: '1px solid var(--border)' }}>{error}</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
